import {
  GrazingOffMovementItem,
  GrazingOnMovementItem,
  LivestockBalanceItem,
  LivestockClass,
  LivestockClassType,
  LivestockMovementItem,
  LivestockTypeName,
} from 'api/models'
import { differenceInDays, isAfter, isBefore, isEqual } from 'date-fns'
import { useMemo } from 'react'
import { parseISODateInputVal } from 'utils/date'
import { coerceToNumber, safeSum } from 'utils/formatting'
import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'

export function useStockUnitOpenCloseCalculation(
  livestockClasses: LivestockClass[],
  balanceItems: LivestockBalanceItem[]
) {
  const stockUnitsAtOpen = useMemo(() => {
    return balanceItems.reduce((acc, item) => {
      const stockClass = livestockClasses.find((clz) => clz.id === item.classification)

      acc[item.classification] = stockClass
        ? Math.round(
            stockClass.su_value *
              (coerceToNumber(item.owned_on_at_open) + coerceToNumber(item.grazed_on_at_open))
          )
        : 0

      return acc
    }, {} as Record<number, number>)
  }, [livestockClasses, balanceItems])

  const stockUnitsAtClose = useMemo(() => {
    return balanceItems.reduce((acc, item) => {
      const stockClass = livestockClasses.find((clz) => clz.id === item.classification)
      acc[item.classification] = stockClass
        ? Math.round(
            stockClass.su_value *
              (coerceToNumber(item.owned_on_at_close) + coerceToNumber(item.grazed_on_at_close))
          )
        : 0
      return acc
    }, {} as Record<number, number>)
  }, [livestockClasses, balanceItems])

  return { stockUnitsAtOpen, stockUnitsAtClose }
}

export function useOnHandOpenCloseCalculation(balanceItems: LivestockBalanceItem[]) {
  const onHandAtOpen = useMemo(() => {
    return balanceItems.reduce((acc, item) => {
      acc[item.classification] =
        coerceToNumber(item.owned_on_at_open) + coerceToNumber(item.grazed_on_at_open)
      return acc
    }, {} as Record<number, number>)
  }, [balanceItems])

  const onHandAtClose = useMemo(() => {
    return balanceItems.reduce((acc, item) => {
      acc[item.classification] =
        coerceToNumber(item.owned_on_at_close) + coerceToNumber(item.grazed_on_at_close)
      return acc
    }, {} as Record<number, number>)
  }, [balanceItems])

  return { onHandAtOpen, onHandAtClose }
}

/**
 * ON FARM GRAZING "TIME ON FARM"
 */
export function useGrazingOnTimeOnFarmCalculation(
  grazingOnItem: GrazingOnMovementItem | null | undefined,
  seasonStart: Date,
  seasonEnd: Date
): null | number {
  if (!grazingOnItem || (!grazingOnItem.tally_arrive && !grazingOnItem.tally_leave)) {
    return null
  }

  let openDate = parseISODateInputVal(grazingOnItem.arrived_date)
  let closeDate = parseISODateInputVal(grazingOnItem.left_date)

  openDate = openDate && isAfter(openDate, seasonStart) ? openDate : seasonStart
  closeDate = closeDate && isBefore(closeDate, seasonEnd) ? closeDate : seasonEnd

  if (isEqual(closeDate, openDate) || isBefore(closeDate, openDate)) {
    return null
  }

  return differenceInDays(closeDate, openDate) / 365
}

/**
 * OFF FARM GRAZING "TIME ON FARM"
 */
export function useGrazingOffTimeOnFarmCalculation(
  grazingOffItem: GrazingOffMovementItem | null | undefined,
  seasonStart: Date,
  seasonEnd: Date
): null | number {
  if (!grazingOffItem || (!grazingOffItem.tally_depart && !grazingOffItem.tally_return)) {
    return null
  }

  let openDate = parseISODateInputVal(grazingOffItem.departed_date)
  let closeDate = parseISODateInputVal(grazingOffItem.returned_date)

  openDate = openDate && isAfter(openDate, seasonStart) ? openDate : seasonStart
  closeDate = closeDate && isBefore(closeDate, seasonEnd) ? closeDate : seasonEnd

  if (isEqual(closeDate, openDate) || isBefore(closeDate, openDate)) {
    return null
  }

  return 1 - differenceInDays(closeDate, openDate) / 365
}

export const useGrazingOnTally = calculateGrazingOnTally
export function calculateGrazingOnTally(
  livestockClassIds: Array<number | null>,
  grazingOnItems: GrazingOnMovementItem[],
  seasonStart: Date,
  seasonEnd: Date
) {
  const items = grazingOnItems.filter(
    (item) => item.classification && livestockClassIds.includes(item.classification)
  )

  return {
    on: items.reduce((acc, iter) => {
      const arrivedDate = parseISODateInputVal(iter.arrived_date)
      if (arrivedDate && (isEqual(arrivedDate, seasonStart) || isAfter(arrivedDate, seasonStart))) {
        acc += iter.tally_arrive || 0
      }
      return acc
    }, 0),
    off: items.reduce((acc, iter) => {
      const leftDate = parseISODateInputVal(iter.left_date)
      if (leftDate && (isEqual(leftDate, seasonEnd) || isBefore(leftDate, seasonEnd))) {
        acc += iter.tally_leave || 0
      }
      return acc
    }, 0),
  }
}

export const useGrazingOffTally = calculateGrazingOffTally
export function calculateGrazingOffTally(
  livestockClassIds: Array<number | null>,
  grazingOffItems: GrazingOffMovementItem[],
  seasonStart: Date,
  seasonEnd: Date
) {
  const items = grazingOffItems.filter(
    (item) => item.classification && livestockClassIds.includes(item.classification)
  )

  return {
    on: items.reduce((acc, iter) => {
      const returnedDate = parseISODateInputVal(iter.returned_date)
      if (returnedDate && (isEqual(returnedDate, seasonEnd) || isBefore(returnedDate, seasonEnd))) {
        acc += iter.tally_return || 0
      }
      return acc
    }, 0),
    off: items.reduce((acc, iter) => {
      const departedDate = parseISODateInputVal(iter.departed_date)
      if (
        departedDate &&
        (isEqual(departedDate, seasonStart) || isAfter(departedDate, seasonStart))
      ) {
        acc += iter.tally_depart || 0
      }
      return acc
    }, 0),
  }
}

export interface LossCalculationResult {
  adultPctg: number
  babyPctg: number
  adultHeads: number
  babyHeads: number
  adultInput: number
  babyInput: number
}
export function useLossCalculation(
  balanceItems: LivestockBalanceItem[],
  movementItems: LivestockMovementItem[],
  grazingOnItems: GrazingOnMovementItem[],
  grazingOffItems: GrazingOffMovementItem[],
  livestockClasses: LivestockClass[],
  livestockClassTypes: LivestockClassType[],
  seasonStart: Date,
  seasonEnd: Date
): Record<LivestockTypeName, LossCalculationResult> {
  const { onHandAtOpen: atOpen, onHandAtClose: atClose } = useOnHandOpenCloseCalculation(
    balanceItems
  )

  return useMemo(() => {
    return calculateLoss(
      movementItems,
      atOpen,
      atClose,
      grazingOnItems,
      grazingOffItems,
      livestockClasses,
      livestockClassTypes,
      seasonStart,
      seasonEnd
    )
  }, [
    movementItems,
    atOpen,
    atClose,
    grazingOnItems,
    grazingOffItems,
    livestockClasses,
    livestockClassTypes,
    seasonStart,
    seasonEnd,
  ])
}

export function calculateLoss(
  movementItems: LivestockMovementItem[],
  atOpen: Record<number, number>,
  atClose: Record<number, number>,
  grazingOnItems: GrazingOnMovementItem[],
  grazingOffItems: GrazingOffMovementItem[],
  livestockClasses: LivestockClass[],
  livestockClassTypes: LivestockClassType[],
  seasonStart: Date,
  seasonEnd: Date
): Record<LivestockTypeName, LossCalculationResult> {
  const itemsGroupedByType: Record<LivestockTypeName, LivestockMovementItem[]> = groupBy(
    movementItems,
    (item) => {
      const stockClass = livestockClasses.find((clz) => clz.id === item.classification)
      const itemType = stockClass
        ? livestockClassTypes.find((t) => t.id === stockClass.livestock_type)
        : null
      return itemType ? itemType.name : ''
    }
  ) as any // eslint-disable-line @typescript-eslint/no-explicit-any

  return mapValues(itemsGroupedByType, (itemsOfType) => {
    const totalOnHandAtOpen = safeSum(itemsOfType.map((item) => atOpen[item.classification]))
    const totalOnHandAtClose = safeSum(itemsOfType.map((item) => atClose[item.classification]))
    const totalPurchases = safeSum(itemsOfType.map((item) => item.purchases))
    const totalGrazingOnTally = calculateGrazingOnTally(
      itemsOfType.map((iter) => iter.classification),
      grazingOnItems,
      seasonStart,
      seasonEnd
    )

    const totalGrazingOffTally = calculateGrazingOffTally(
      itemsOfType.map((iter) => iter.classification),
      grazingOffItems,
      seasonStart,
      seasonEnd
    )

    const birthClassItems = itemsOfType.filter(
      (item) => livestockClasses.find((clz) => clz.id === item.classification)?.is_births_class
    )
    const totalBirthClassPurchases = safeSum(birthClassItems.map((item) => item.purchases))
    const totalBirthClassBirths = safeSum(birthClassItems.map((item) => item.births))
    const birthClassStoreSales = safeSum(birthClassItems.map((item) => item.sales_store))
    const birthClassPrimeSales = safeSum(birthClassItems.map((item) => item.sales_prime))
    const birthClassUsedOnFarm = safeSum(birthClassItems.map((item) => item.used_on_farm))

    const birthClassGrazingOnTally = calculateGrazingOnTally(
      birthClassItems.map((iter) => iter.classification),
      grazingOnItems,
      seasonStart,
      seasonEnd
    )
    const birthClassGrazingOffTally = calculateGrazingOffTally(
      birthClassItems.map((iter) => iter.classification),
      grazingOffItems,
      seasonStart,
      seasonEnd
    )

    const youngClassesOnHandAtClose = safeSum(
      itemsOfType.map((item) => {
        const stockClass = livestockClasses.find((clz) => clz.id === item.classification)
        return stockClass && stockClass.is_young_class && !stockClass.is_births_class
          ? atClose[stockClass.id]
          : 0
      })
    )

    // LOSS BIRTH CLASS
    const lossBirthClassInput =
      totalBirthClassBirths + totalBirthClassPurchases + birthClassGrazingOnTally.on

    const lossBirthClassNum =
      lossBirthClassInput +
      birthClassGrazingOffTally.on -
      birthClassGrazingOffTally.off -
      (birthClassStoreSales +
        birthClassPrimeSales +
        birthClassGrazingOnTally.off +
        birthClassUsedOnFarm +
        youngClassesOnHandAtClose)

    const lossBirthClassPercent = lossBirthClassInput ? lossBirthClassNum / lossBirthClassInput : 0

    // LOSS ADULT
    const totalStoreSales = safeSum(itemsOfType.map((item) => item.sales_store))
    const totalPrimeSales = safeSum(itemsOfType.map((item) => item.sales_prime))
    const totalUsedOnFarm = safeSum(itemsOfType.map((item) => item.used_on_farm))

    const lossAdultInput =
      totalOnHandAtOpen +
      totalPurchases +
      totalGrazingOnTally.on -
      totalBirthClassPurchases -
      birthClassGrazingOnTally.on

    const lossAdultNum =
      totalOnHandAtOpen +
      totalBirthClassBirths +
      totalPurchases +
      totalGrazingOnTally.on +
      totalGrazingOffTally.on -
      totalGrazingOnTally.off -
      totalGrazingOffTally.off -
      totalStoreSales -
      totalPrimeSales -
      totalUsedOnFarm -
      totalOnHandAtClose -
      lossBirthClassNum

    const lossAdultPercent = lossAdultInput ? lossAdultNum / lossAdultInput : 0

    return {
      adultPctg: lossAdultPercent,
      babyPctg: lossBirthClassPercent,
      adultHeads: lossAdultNum,
      babyHeads: lossBirthClassNum,
      adultInput: lossAdultInput,
      babyInput: lossBirthClassInput,
    }
  })
}
